/**
 * Behaviour shared by the header drop-down menus (Help #776, Settings #2242): open/closed state, close on a click or
 * touch outside and on Escape, and the first menu item takes the focus when the menu opens.
 */
import { useEffect, useRef, useState, type RefObject } from 'react';

export interface MenuState<T extends HTMLElement> {
  open: boolean;
  setOpen: (open: boolean | ((o: boolean) => boolean)) => void;
  /** The element holding the button and the menu: clicks inside it do not close the menu. */
  ref: RefObject<T>;
}

export function useMenu<T extends HTMLElement = HTMLDivElement>(): MenuState<T> {
  const [open, setOpen] = useState(false);
  const ref = useRef<T>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    document.addEventListener('keydown', onKey);
    ref.current?.querySelector<HTMLElement>('[role^="menuitem"]')?.focus();
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return { open, setOpen, ref };
}
